import { CSSObject } from '@emotion/react'
import {
  Color,
  CornerRadius,
  cssVar,
  FocusIndicator,
  FontWeight,
  GemColors,
  Spacing,
  ThemedColors,
  Transition,
  wideGamutColor,
} from '@opallabs/gem'
import { modernWithFallback } from '@opallabs/lambda'
import { WithRequiredTheme } from '@opallabs/lapidary'

export const editorContainer: CSSObject = {
  inlineSize: '100%',
  maxBlockSize: 'inherit',
  position: 'relative',
  userSelect: 'text',
  cursor: 'auto',
}

export const editorContentWrapper: CSSObject = {
  maxBlockSize: 'inherit',
  overflow: 'auto',

  // Overrides legacy selection text color when selection is hidden
  '.ProseMirror-hideselection ::selection': {
    color: 'inherit',
  },
  '.ProseMirror-hideselection ::-moz-selection': {
    color: 'inherit',
  },
}

export const editorContent = ({ theme }: WithRequiredTheme): CSSObject => ({
  fontSize: '16px',
  lineHeight: '1.5',

  '&:focus': {
    outline: 0,
  },

  'p.is-editor-empty:first-of-type::before': {
    opacity: 0.4,
    content: 'attr(data-placeholder)',
    float: 'inline-start',
    blockSize: 0,
    pointerEvents: 'none',
  },

  // Default text styles
  a: {
    color: Color[theme].Text.Accent,
  },
  p: {
    lineHeight: '1.5',
    marginBlockStart: '1em',
    marginBlockEnd: '0.75em',
  },
  'h1, h2, h3, h4': {
    lineHeight: '1.2',
    marginBlockStart: '1em',
    marginBlockEnd: '0.5em',
  },
  h1: {
    fontSize: '36px',
  },
  h2: {
    fontSize: '30px',
  },
  h3: {
    fontSize: '24px',
  },
  h4: {
    fontSize: '20px',
  },
  // The `hr` styles can be finicky in legacy views like
  // the content composer due to Supernova overrides. Be
  // careful when making changes here.
  hr: {
    border: `1px solid ${Color[theme].Surface.N400}`,
    blockSize: 0,
    marginBlock: '16px',
    marginInline: '0',
    position: 'initial',
  },
  'ul, ol': {
    marginBlock: '16px',
    marginInline: '0',
    paddingInlineStart: '40px',
    listStylePosition: 'inside',
  },
  'li > p': {
    marginBlock: '8px',
    display: 'inline',
  },
  blockquote: {
    borderInlineStartColor: Color[theme].Text.Gray,
    color: Color[theme].Text.Gray,
    borderInlineStartWidth: '2px',
    borderInlineStartStyle: 'solid',
    paddingBlock: '1px',
    paddingInlineEnd: '0',
    paddingInlineStart: '20px',
    marginBlock: '5px',
    marginInline: '0',
    fontStyle: 'italic',
  },
  '.tableWrapper': {
    display: 'flex',
    justifyContent: 'center',
    // IMPORTANT: The use of `minWidth` below allows the
    // table to grow outside the left bound of the document
    minInlineSize: 'inherit',
  },
  '.tableResizeWrapper': {
    flexShrink: 0,
    ...modernWithFallback(
      [{ overflowInline: 'auto' }, { overflowX: 'auto' }],
      'overflow-inline: auto'
    ),
    // IMPORTANT: The use of `minWidth` below allows the
    // table to be properly placed for the document, especially
    // in browsers like Firefox.
    minInlineSize: '100%',
  },
  table: {
    borderCollapse: 'separate',
    borderSpacing: 0,
    maxInlineSize: '100%',
    '&:hover': {
      cursor: 'ew-resize',
    },
    th: {
      backgroundColor: wideGamutColor(GemColors.N050),
      fontWeight: FontWeight.Bold,
      textAlign: 'start',
    },
    'td, th': {
      padding: Spacing.XS * 3,
      position: 'relative',
      verticalAlign: 'top',
      borderInlineEnd: `solid 1px ${Color[theme].Surface.N400}`,
      borderBlockEnd: `solid 1px ${Color[theme].Surface.N400}`,

      '&.selectedCell': {
        outline: `solid 1px ${Color[theme].Surface.A200}`,
      },
      '&:hover.has-focus': {
        cursor: 'auto',
      },
      '&:hover:not(.has-focus)': {
        cursor: 'pointer',
        backgroundColor: Color[theme].Translucent.A08,
      },
      p: {
        margin: 0,
      },
      '.node-asset > div': {
        padding: 0,
      },
    },

    // NOTE: `table` uses borderCollapse: 'separate' with selective borders to
    // achieve rounded corners, as borderCollapse: 'collapse' doesn't support that.
    'tr:first-child td, tr:first-child th': {
      borderBlockStart: `solid 1px ${Color[theme].Surface.N400}`,
    },
    'tr td:first-child, tr th:first-child': {
      borderInlineStart: `solid 1px ${Color[theme].Surface.N400}`,
    },
    'tr:first-child th:first-child, tr:first-child td:first-child': {
      borderStartStartRadius: Spacing.XS,
    },
    'tr:first-child th:last-child, tr:first-child td:last-child': {
      borderStartEndRadius: Spacing.XS,
    },
    'tr:last-child td:first-child': {
      borderEndStartRadius: Spacing.XS,
    },
    'tr:last-child td:last-child': {
      borderEndEndRadius: Spacing.XS,
    },

    '.column-resize-handle': {
      cursor: 'ew-resize',
      insetBlockEnd: -1,
      position: 'absolute',
      insetInlineEnd: -1,
      insetBlockStart: 0,
      inlineSize: Spacing.XS,
      backgroundColor: Color[theme].Surface.A200,
    },
  },
  // Disable table interaction styles when read-only
  '&[contenteditable="false"] table': {
    '&:hover': {
      cursor: 'default',
    },
    'td, th': {
      '&:hover:not(.has-focus)': {
        backgroundColor: 'transparent',
        cursor: 'default',
      },
    },
  },
  // prosemirror-tables adds `resize-cursor` while a column handle is active.
  // This must come after the cell `:hover` cursors above, which it overrides
  // at equal specificity, or the resize cursor only shows over the 1px border.
  '&.resize-cursor table': {
    'td, th': {
      '&, &:hover': {
        cursor: 'ew-resize',
      },
    },
  },
  '.text-center-justify': {
    marginInline: 'auto',
    maxInlineSize: '640px',
    display: 'block',
  },
  '.task-list': {
    listStyleType: 'none',
    '.task-list': {
      marginBlock: 0,
    },
    'li[data-checked] > label': {
      display: 'inline-flex',
      marginInlineEnd: Spacing.XS,
      verticalAlign: 'text-top',
    },
    'li[data-checked] > div': {
      display: 'inline',
    },
    // Matches Lapidary Checkbox styles
    'input[type="checkbox"]': {
      appearance: 'none',
      blockSize: Spacing.M,
      inlineSize: Spacing.M,
      border: `${Spacing.XS / 2}px solid ${cssVar(ThemedColors.StrokeSecondary2)}`,
      borderRadius: CornerRadius.Small,
      backgroundColor: 'transparent',
      cursor: 'pointer',
      margin: 0,
      position: 'relative',
      boxSizing: 'border-box',
      ...Transition(['border-color', 'background-color', 'box-shadow']),
      '&:checked': {
        backgroundColor: cssVar(ThemedColors.Primary6),
        borderColor: cssVar(ThemedColors.Primary6),
      },
      '&:checked::before': {
        content: '""',
        display: 'block',
        position: 'absolute',
        insetInlineStart: 3,
        insetBlockStart: 0,
        inlineSize: 4,
        blockSize: 8,
        borderStyle: 'solid',
        borderColor: cssVar(ThemedColors.TextContrast),
        borderWidth: '0 2px 2px 0',
        transform: 'rotate(45deg)',
      },
      '&:hover:enabled, &:focus:enabled': {
        borderColor: cssVar(ThemedColors.Primary6),
      },
      '&:focus': {
        ...FocusIndicator[theme],
      },
      '&:disabled': {
        cursor: 'not-allowed',
        opacity: 0.5,
      },
    },
    'li p': {
      display: 'inline',
      margin: 0,
      // without this the cursor is not visible when a new task
      // item is first created
      minInlineSize: '1px',
    },
  },
  // An empty list item paragraph holds nothing but ProseMirror's trailing
  // break. As an inline box it shares the list item's line box with the
  // marker or the non-editable task checkbox, and Safari then paints the
  // caret at the start of the list item, to the left of the marker or
  // checkbox (ProseMirror issue #1165). A 1px inline-block box gives the break
  // a line box of its own. Only empty paragraphs match, so the wrapping of
  // multiline items and the alignment of items with text stay the same. Keep
  // it after `.task-list` so it overrides `.task-list li p` by order too.
  'li p:has(> br.ProseMirror-trailingBreak:only-child)': {
    display: 'inline-block',
    minInlineSize: '1px',
    marginBlock: 0,
  },
  '.dropcursor': {
    color: Color[theme].Surface.A200,
  },
  '.selection': {
    // https://developer.mozilla.org/en-US/docs/Web/CSS/system-color
    backgroundColor: 'Highlight',
    color: 'HighlightText',
  },

  // Styles for highlight mark
  mark: {
    backgroundColor: 'inherit',
  },

  // Styles for TipTap's ResizableNodeView handles
  '[data-resize-container]': {
    display: 'inline-block !important', // Allows other nodes on the same line
    padding: '8px',
    maxInlineSize: 'fit-content !important',
    verticalAlign: 'top',
  },
  '[data-resize-wrapper]': {
    display: 'inline-block',
  },
  '[data-resize-handle]': {
    position: 'absolute',
    zIndex: 10,
    inlineSize: '12px',
    blockSize: '12px',
    cursor: 'pointer',

    '&::after': {
      content: '""',
      position: 'absolute',
      insetBlockStart: '50%',
      insetInlineStart: '50%',
      transform: 'translate(-50%, -50%)',
      inlineSize: '8px',
      blockSize: '8px',
      backgroundColor: Color[theme].Surface.A200,
      borderRadius: '50%',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
      transition: 'transform 0.1s ease, background-color 0.1s ease',
    },

    '&:hover::after': {
      backgroundColor: Color[theme].Surface.A200,
      transform: 'translate(-50%, -50%) scale(1.5)',
    },
  },

  // ResizableNodeView handle positions
  // Note: `!important` being used to override defaults set by TipTap's ResizableNodeView
  '[data-resize-handle="top-right"]': {
    insetBlockStart: '-4px !important',
    insetInlineEnd: '-4px !important',
    cursor: 'nesw-resize',
  },
  '[data-resize-handle="top-left"]': {
    insetBlockStart: '-4px !important',
    insetInlineStart: '-4px !important',
    cursor: 'nwse-resize',
  },
  '[data-resize-handle="bottom-right"]': {
    insetBlockEnd: '4px !important',
    insetInlineEnd: '-4px !important',
    cursor: 'nwse-resize',
  },
  '[data-resize-handle="bottom-left"]': {
    insetBlockEnd: '4px !important',
    insetInlineStart: '-4px !important',
    cursor: 'nesw-resize',
  },
})
